class HLOStorefront {
  constructor() {
    this.bindDrawers();
    this.bindDetails();
    this.bindGalleries();
    this.bindVariantPickers();
    this.bindQuantity();
    this.bindStickyPurchase();
    this.bindAjaxCart();
    this.bindCartPage();
    this.bindDiscountCode();
    this.bindQuickView();
    this.bindCollectionView();
  }

  bindDrawers() {
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-drawer-open]');
      if (opener) {
        const drawer = document.getElementById(opener.dataset.drawerOpen);
        if (drawer?.showModal) {
          drawer._trigger = opener;
          drawer.showModal();
          opener.setAttribute('aria-expanded', 'true');
          requestAnimationFrame(() => drawer.querySelector('[data-drawer-title]')?.focus());
        }
      }
      const closer = event.target.closest('[data-drawer-close]');
      if (closer) closer.closest('dialog')?.close();
      const submenuOpener = event.target.closest('[data-submenu-open]');
      if (submenuOpener) {
        const panel = document.getElementById(submenuOpener.dataset.submenuOpen);
        if (panel) {
          panel._trigger = submenuOpener;
          panel.hidden = false;
          requestAnimationFrame(() => panel.querySelector('[tabindex="-1"]')?.focus());
        }
      }
      const submenuCloser = event.target.closest('[data-submenu-close]');
      if (submenuCloser) {
        const panel = submenuCloser.closest('.mobile-submenu');
        panel.hidden = true;
        panel._trigger?.focus();
      }
    });
    document.querySelectorAll('dialog[data-drawer]').forEach((drawer) => {
      drawer.addEventListener('click', (event) => {
        if (event.target === drawer) drawer.close();
      });
      drawer.addEventListener('close', () => {
        drawer.querySelectorAll('.mobile-submenu').forEach((panel) => { panel.hidden = true; });
        document.querySelector(`[data-drawer-open="${drawer.id}"]`)?.setAttribute('aria-expanded', 'false');
        drawer._trigger?.focus();
      });
      drawer.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const panel = drawer.querySelector('.mobile-submenu:not([hidden])');
        if (panel) {
          event.preventDefault();
          event.stopPropagation();
          panel.hidden = true;
          panel._trigger?.focus();
        }
      });
    });
  }

  bindDetails() {
    document.querySelectorAll('.mega').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        document.querySelectorAll('.mega[open]').forEach((other) => {
          if (other !== details) other.removeAttribute('open');
        });
      });
    });
    document.addEventListener('click', (event) => {
      if (event.target.closest('.mega')) return;
      document.querySelectorAll('.mega[open]').forEach((details) => details.removeAttribute('open'));
    });
  }

  bindVariantPickers(root = document) {
    root.querySelectorAll('[data-option-picker]').forEach((picker) => {
      if (picker.dataset.bound) return;
      picker.dataset.bound = 'true';
      picker.addEventListener('change', (event) => this.onOptionChange(event));
      const preload = (event) => {
        let source = event.target.matches?.('[data-option-select]') ? event.target.selectedOptions[0] : event.target.closest?.('[data-option-value-id]');
        if (!source) {
          const label = event.target.closest?.('label[for]');
          if (label) source = picker.querySelector(`#${CSS.escape(label.htmlFor)}`);
        }
        if (source?.dataset.variantImage) {
          const image = new Image();
          image.src = source.dataset.variantImage;
        }
      };
      picker.addEventListener('pointerover', preload);
      picker.addEventListener('pointerdown', preload);
      picker.addEventListener('focusin', preload);
      const preloadCount = Number(picker.dataset.preloadCount || 0);
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (preloadCount > 0 && !connection?.saveData && !['slow-2g', '2g'].includes(connection?.effectiveType)) {
        const sources = [...new Set([...picker.querySelectorAll('[data-variant-image]')].map((node) => node.dataset.variantImage).filter(Boolean))].slice(0, preloadCount);
        const loadImages = () => sources.forEach((source, index) => setTimeout(() => { const image = new Image(); image.src = source; }, index * 120));
        if ('requestIdleCallback' in window) requestIdleCallback(loadImages, { timeout: 1800 });
        else setTimeout(loadImages, 800);
      }
    });
  }

  bindGalleries(root = document) {
    root.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.bound) return;
      gallery.dataset.bound = 'true';

      const syncVariantToMedia = (mediaId) => {
        if (gallery.dataset.syncVariant !== 'true') return;
        const productSection = gallery.closest('[data-product-section]');
        if (!productSection) return;

        const selector = `[data-option-picker] [data-variant-media-id="${CSS.escape(String(mediaId))}"]`;
        const controls = [...productSection.querySelectorAll(selector)];
        const availableMatches = controls.filter((control) => {
          if (control.matches('option')) return !control.disabled && !control.selected;
          return !control.disabled && !control.classList.contains('disabled') && !control.checked;
        });
        const match = availableMatches.find((control) => control.closest('.option-values--swatch')) || availableMatches[0];
        if (!match) return;

        if (match.matches('option')) {
          const select = match.closest('select');
          if (!select) return;
          select.value = match.value;
          select.dataset.gallerySync = 'true';
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        match.checked = true;
        match.dataset.gallerySync = 'true';
        match.dispatchEvent(new Event('change', { bubbles: true }));
      };

      const show = (mediaId, announce = true, syncVariant = false) => {
        const slides = [...gallery.querySelectorAll('[data-media-slide]')];
        const target = slides.find((slide) => slide.dataset.mediaSlide === String(mediaId));
        if (!target) return;
        slides.forEach((slide) => {
          const active = slide === target;
          slide.hidden = !active;
          slide.classList.toggle('is-active', active);
        });
        gallery.querySelectorAll('[data-media-target]').forEach((button) => {
          const active = button.dataset.mediaTarget === String(mediaId);
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-current', String(active));
        });
        gallery.dataset.activeMediaId = String(mediaId);
        if (syncVariant) syncVariantToMedia(mediaId);
        if (announce) document.getElementById('a11y-status').textContent = 'Product image updated.';
      };
      gallery.addEventListener('click', (event) => {
        const thumbnail = event.target.closest('[data-media-target]');
        if (thumbnail) show(thumbnail.dataset.mediaTarget, true, event.isTrusted);
        const arrow = event.target.closest('[data-gallery-step]');
        if (arrow) {
          const slides = [...gallery.querySelectorAll('[data-media-slide]')];
          const currentIndex = slides.findIndex((slide) => !slide.hidden);
          const nextIndex = (currentIndex + Number(arrow.dataset.galleryStep) + slides.length) % slides.length;
          show(slides[nextIndex].dataset.mediaSlide, true, event.isTrusted);
        }
      });
      show(gallery.dataset.activeMediaId, false);
    });
  }

  async onOptionChange(event) {
    const control = event.target;
    const input = control.matches('[data-option-select]') ? control.selectedOptions[0] : control.closest('[data-option-value-id]');
    if (!input) return;
    const fromGallery = control.dataset.gallerySync === 'true';
    delete control.dataset.gallerySync;
    const productSection = control.closest('[data-product-section]');
    const picker = control.closest('[data-option-picker]');
    const selectedIds = [
      ...[...picker.querySelectorAll('input[data-option-value-id]:checked')].map((node) => node.dataset.optionValueId),
      ...[...picker.querySelectorAll('select[data-option-select]')].map((select) => select.selectedOptions[0]?.dataset.optionValueId).filter(Boolean)
    ];
    const currentProductUrl = productSection.dataset.productUrl;
    const nextProductUrl = input.dataset.productUrl || currentProductUrl;
    if (input.dataset.variantImage) {
      const image = new Image();
      image.src = input.dataset.variantImage;
    }
    if (input.dataset.variantMediaId) productSection.querySelector(`[data-media-target="${CSS.escape(input.dataset.variantMediaId)}"]`)?.click();
    const url = new URL(nextProductUrl, window.location.origin);
    url.searchParams.set('section_id', productSection.dataset.sectionId);
    if (selectedIds.length) url.searchParams.set('option_values', selectedIds.join(','));
    productSection._variantAbort?.abort();
    productSection._variantAbort = new AbortController();
    productSection.setAttribute('aria-busy', 'true');
    productSection.querySelectorAll('[data-primary-add],[data-sticky-add]').forEach((button) => {
      if (!button.hasAttribute('data-variant-update-disabled')) button.dataset.variantUpdateDisabled = String(button.disabled);
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    });
    try {
      const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' }, signal: productSection._variantAbort.signal });
      if (!response.ok) throw new Error(`Variant request failed: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = html.querySelector('[data-product-section]');
      if (!replacement) throw new Error('Product section missing from response');
      const focusId = control.id;
      const sameProduct = new URL(nextProductUrl, window.location.origin).pathname === new URL(currentProductUrl, window.location.origin).pathname;
      if (sameProduct) {
        const currentGallery = productSection.querySelector('[data-product-gallery]');
        replacement.querySelector('[data-product-gallery]')?.replaceWith(currentGallery);
      }
      productSection.replaceWith(replacement);
      this.bindGalleries(replacement);
      this.bindVariantPickers(replacement);
      this.bindQuantity(replacement);
      this.bindStickyPurchase(replacement.ownerDocument);
      if (!fromGallery) replacement.querySelector(`#${CSS.escape(focusId)}`)?.focus();
      const canonicalUrl = replacement.dataset.productUrl;
      const variantId = replacement.querySelector('[name="id"]')?.value;
      if (canonicalUrl && variantId) history.replaceState({}, '', `${canonicalUrl}?variant=${variantId}`);
      document.getElementById('a11y-status').textContent = fromGallery ? 'Matching product option selected for this image.' : 'Product options and availability updated.';
    } catch (error) {
      if (error.name === 'AbortError') return;
      productSection.removeAttribute('aria-busy');
      productSection.querySelectorAll('[data-variant-update-disabled]').forEach((button) => {
        button.disabled = button.dataset.variantUpdateDisabled === 'true';
        button.removeAttribute('data-variant-update-disabled');
        button.removeAttribute('aria-busy');
      });
      document.getElementById('a11y-status').textContent = 'Options could not be updated. Please try again.';
    }
  }

  bindQuantity(root = document) {
    root.querySelectorAll('[data-quantity-stepper]').forEach((stepper) => {
      if (stepper.dataset.bound) return;
      stepper.dataset.bound = 'true';
      const input = stepper.querySelector('input');
      stepper.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-step]');
        if (!button) return;
        const increment = Number(input.step) || 1;
        const minimum = Number(input.min) || increment;
        const maximum = Number(input.max) || Infinity;
        input.value = Math.min(maximum, Math.max(minimum, Number(input.value) + Number(button.dataset.step) * increment));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  bindStickyPurchase(root = document) {
    root.querySelectorAll('[data-sticky-add]').forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => document.querySelector('[data-primary-add]')?.click());
    });
  }

  bindAjaxCart() {
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('form[action*="/cart/add"]');
      const submitter = event.submitter;
      if (!form || !submitter?.matches('[data-primary-add],[data-cart-add-submit]')) return;
      event.preventDefault();
      submitter.disabled = true;
      submitter.setAttribute('aria-busy', 'true');
      try {
        const drawer = document.querySelector('[data-cart-drawer]');
        const formData = new FormData(form);
        if (drawer?.dataset.sectionId) {
          formData.append('sections', drawer.dataset.sectionId);
          formData.append('sections_url', `${window.location.pathname}${window.location.search}`);
        }
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, { method: 'POST', body: formData, headers: { Accept: 'application/json' } });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'This item could not be added.');
        const renderedDrawer = drawer?.dataset.sectionId ? result.sections?.[drawer.dataset.sectionId] : null;
        if (renderedDrawer) this.replaceCartDrawer(renderedDrawer, { trigger: submitter, open: true });
        else await this.refreshCartDrawer(submitter, true);
        document.getElementById('a11y-status').textContent = `${result.product_title || 'Product'} added to cart.`;
      } catch (error) {
        document.getElementById('a11y-status').textContent = error.message;
        submitter.disabled = false;
      } finally {
        submitter.removeAttribute('aria-busy');
        submitter.disabled = false;
      }
    });

    document.addEventListener('click', (event) => {
      const stepButton = event.target.closest('[data-cart-quantity-step]');
      const removeButton = event.target.closest('[data-cart-remove]');
      if (!stepButton && !removeButton) return;
      const item = event.target.closest('[data-cart-line]');
      const input = item?.querySelector('.cart-stepper input');
      if (!item || !input) return;
      const previousQuantity = Number(item.dataset.cartQuantity || input.value);
      const quantity = removeButton ? 0 : Math.max(0, Number(input.value) + Number(stepButton.dataset.cartQuantityStep));
      input.value = quantity;
      item.dataset.requestedQuantity = String(quantity);
      item.classList.add('is-updating');
      item.closest('[data-cart-drawer]')?.setAttribute('aria-busy', 'true');
      document.getElementById('a11y-status').textContent = quantity === 0 ? 'Removing item…' : `Quantity ${quantity}, updating price…`;
      clearTimeout(item._cartChangeTimer);
      item._cartChangeTimer = setTimeout(() => this.commitQuickCartChange(item, previousQuantity, stepButton?.dataset.cartQuantityStep), 90);
    });
  }

  async commitQuickCartChange(item, previousQuantity, focusStep) {
    if (!item.isConnected) return;
    const drawer = item.closest('[data-cart-drawer]');
    const input = item.querySelector('.cart-stepper input');
    const quantity = Number(item.dataset.requestedQuantity);
    const sectionId = drawer?.dataset.sectionId;
    const controls = item.querySelectorAll('[data-cart-quantity-step], [data-cart-remove]');
    controls.forEach((control) => { control.disabled = true; });
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: item.dataset.cartLine,
          quantity,
          sections: sectionId,
          sections_url: `${window.location.pathname}${window.location.search}`
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Cart could not be updated.');
      const renderedDrawer = sectionId ? result.sections?.[sectionId] : null;
      if (renderedDrawer) {
        this.replaceCartDrawer(renderedDrawer, { open: true, focusKey: quantity > 0 ? item.dataset.cartLine : null, focusStep });
      } else {
        await this.refreshCartDrawer(null, true);
      }
      this.updateCartCount(result.item_count);
      document.getElementById('a11y-status').textContent = quantity === 0 ? 'Item removed from cart.' : 'Cart quantity and price updated.';
    } catch (error) {
      if (input) input.value = previousQuantity;
      item.classList.remove('is-updating');
      drawer?.removeAttribute('aria-busy');
      controls.forEach((control) => { control.disabled = false; });
      document.getElementById('a11y-status').textContent = error.message;
    }
  }

  bindCartPage() {
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-main-cart-form]');
      const submitter = event.submitter;
      if (!form || submitter?.name !== 'update') return;
      event.preventDefault();
      const updates = {};
      form.querySelectorAll('[data-cart-line-key]').forEach((input) => {
        updates[input.dataset.cartLineKey] = Math.max(0, Number(input.value) || 0);
      });
      submitter.disabled = true;
      submitter.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates, note: form.querySelector('[name="note"]')?.value || '' })
        });
        const cartState = await response.json();
        if (!response.ok) throw new Error(cartState.description || 'Cart could not be updated.');
        await this.refreshMainCart(cartState);
        this.updateCartCount(cartState.item_count);
        document.getElementById('a11y-status').textContent = 'Cart and subtotal updated.';
      } catch (error) {
        submitter.disabled = false;
        submitter.removeAttribute('aria-busy');
        document.getElementById('a11y-status').textContent = error.message;
      }
    });
  }

  async refreshMainCart(cartState) {
    const currentSection = document.querySelector('[data-main-cart]');
    if (!currentSection) return;
    const url = new URL(window.location.href);
    url.search = '';
    url.searchParams.set('section_id', currentSection.dataset.sectionId);
    const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error('The updated cart could not be displayed.');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const nextSection = html.querySelector('[data-main-cart]');
    if (!nextSection) {
      window.location.reload();
      return;
    }
    currentSection.replaceWith(nextSection);
    this.bindQuantity(nextSection);
    this.updateCartCount(cartState.item_count);
  }

  bindDiscountCode() {
    document.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-quick-checkout-form]');
      if (!form) return;
      const codeInput = form.querySelector('[name="discount_code"]');
      const code = codeInput?.value.trim();
      if (!code) {
        if (event.submitter?.name === 'apply_discount') {
          event.preventDefault();
          codeInput?.focus();
          document.getElementById('a11y-status').textContent = 'Enter a discount code first.';
        }
        return;
      }
      event.preventDefault();
      const root = window.Shopify.routes.root;
      const checkoutPath = `${root}checkout`;
      window.location.assign(`${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(checkoutPath)}`);
    });
  }

  bindQuickView() {
    document.addEventListener('click', async (event) => {
      const trigger = event.target.closest('[data-quick-view-url]');
      if (!trigger) return;
      const dialog = document.getElementById('QuickViewDialog');
      const container = dialog?.querySelector('[data-quick-view-container]');
      if (!dialog || !container) return;
      dialog._trigger = trigger;
      container.innerHTML = '<h2 id="QuickViewTitle" data-drawer-title tabindex="-1">Loading product…</h2>';
      dialog.showModal();
      requestAnimationFrame(() => container.querySelector('[data-drawer-title]')?.focus());
      try {
        const url = new URL(trigger.dataset.quickViewUrl, window.location.origin);
        url.searchParams.set('section_id', 'quick-view');
        const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error('Quick view could not be loaded.');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = html.querySelector('[data-quick-view-content]');
        if (!content) throw new Error('Quick view is unavailable for this product.');
        container.innerHTML = content.outerHTML;
        requestAnimationFrame(() => container.querySelector('[data-drawer-title]')?.focus());
      } catch (error) {
        container.innerHTML = `<div class="quick-view-error"><h2 id="QuickViewTitle" data-drawer-title tabindex="-1">Quick view unavailable</h2><p>${error.message}</p><a class="button" href="${trigger.dataset.quickViewUrl}">Open product</a></div>`;
        container.querySelector('[data-drawer-title]')?.focus();
      }
    });
  }

  async refreshCartDrawer(trigger, open = true) {
    const currentDrawer = document.querySelector('[data-cart-drawer]');
    if (!currentDrawer) throw new Error('The quick cart is unavailable.');
    const url = new URL(window.location.href);
    url.search = '';
    url.searchParams.set('section_id', currentDrawer.dataset.sectionId || 'cart-drawer');
    const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error('The quick cart could not be loaded.');
    this.replaceCartDrawer(await response.text(), { trigger, open });
  }

  replaceCartDrawer(markup, { trigger = null, open = true, focusKey = null, focusStep = null } = {}) {
    const html = new DOMParser().parseFromString(markup, 'text/html');
    const nextDrawer = html.querySelector('[data-cart-drawer]');
    const currentDrawer = document.querySelector('[data-cart-drawer]');
    if (!nextDrawer || !currentDrawer) throw new Error('The quick cart is unavailable.');
    if (currentDrawer.open) currentDrawer.close();
    currentDrawer.replaceWith(nextDrawer);
    const count = Number(nextDrawer.dataset.cartCount);
    this.updateCartCount(count);
    if (open) {
      trigger?.closest('dialog[open]')?.close();
      nextDrawer._trigger = trigger;
      nextDrawer.addEventListener('close', () => nextDrawer._trigger?.focus(), { once: true });
      nextDrawer.addEventListener('click', (event) => { if (event.target === nextDrawer) nextDrawer.close(); });
      nextDrawer.showModal();
      requestAnimationFrame(() => {
        const item = focusKey ? nextDrawer.querySelector(`[data-cart-line="${CSS.escape(focusKey)}"]`) : null;
        const focusTarget = item && focusStep ? item.querySelector(`[data-cart-quantity-step="${CSS.escape(focusStep)}"]`) : null;
        (focusTarget || nextDrawer.querySelector('[data-drawer-title]'))?.focus();
      });
    }
    return nextDrawer;
  }

  updateCartCount(count) {
    const countBubble = document.querySelector('.cart-count');
    if (!countBubble) return;
    countBubble.textContent = count;
    countBubble.hidden = count === 0;
    countBubble.setAttribute('aria-label', `${count} items`);
  }

  bindCollectionView() {
    document.querySelectorAll('[data-view]').forEach((button) => {
      button.addEventListener('click', () => {
        const list = document.querySelector('[data-product-grid]');
        const isList = button.dataset.view === 'list';
        list?.classList.toggle('is-list', isList);
        document.querySelectorAll('[data-view]').forEach((control) => control.setAttribute('aria-pressed', String(control === button)));
        localStorage.setItem('hlo-collection-view', button.dataset.view);
      });
    });
    if (localStorage.getItem('hlo-collection-view') === 'list') document.querySelector('[data-view="list"]')?.click();
  }
}

document.addEventListener('DOMContentLoaded', () => new HLOStorefront());
